import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { workbenchApi } from '../../api/workbench'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import type { Pick } from '../../types/workbench'
import { PickCard, Refusal } from './WorkbenchParts'

// Who to work next. The API ranks; the field above is the same stage the account gets — you at the centre, each
// pick a face sized by its rank, at its account — and the cards below carry the rank, its reasons and its vetoes
// so the rep reads the vetoes BEFORE any research is spent on a person we should not be writing to. Hovering a
// card lights the person on the field; clicking either opens the workbench on that account and person. "Not now"
// cuts one, with why, and the next in line moves up.

export function Picker() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [lit, setLit] = useState<string | null>(null)
  const q = useQuery({ queryKey: ['workbench-picks', spoke], queryFn: () => workbenchApi.picks(spoke), staleTime: 30_000 })
  const cut = useMutation({
    mutationFn: ({ personId, reason }: { personId: string; reason: string }) => workbenchApi.cut(spoke, personId, reason),
    onSuccess: (view) => { qc.setQueryData(['workbench-picks', spoke], view) },
  })
  const open = (p: Pick) => navigate(`/workbench/${encodeURIComponent(p.companyId)}${p.personId ? `?person=${encodeURIComponent(p.personId)}` : ''}`)

  return (
    <section className="page workbench picker">
      <header className="deals-head">
        <div>
          <p className="kicker">Your own · Crafting</p>
          <h1>Who to work next</h1>
          <p className="lede">Five people, ranked, with the reason behind each rank and anything that says don't. Open one to see the account around them, talk it through, and leave with a message in your <Link to="/outbox">Outbox</Link> — nothing sends from here.</p>
        </div>
        <div className="actions"><Link className="button" to="/outreach">Who to contact today</Link></div>
      </header>

      {q.isError && <Refusal title="Nothing to propose yet" message={q.error instanceof RefusedError ? q.error.refusal.message : q.error.message} />}
      {cut.isError && <p className="error small" role="alert">{cut.error instanceof RefusedError ? cut.error.refusal.message : cut.error.message}</p>}

      {q.isPending && <p className="muted" role="status">Weighing everyone you know: the ties, the pursuits, the mail, who else at the firm holds the door…</p>}

      {q.isSuccess && (
        <>
          {q.data.picks.length === 0 && <p className="muted">No one to propose: everyone with a warm tie has been written to lately, or is on hold. Check back tomorrow.</p>}
          <ol className="plain pick-cards">
            {q.data.picks.map((p, i) => (
              <PickCard key={`${p.companyId}:${p.personId ?? ''}`} pick={p} rank={i + 1} lit={!!p.personId && p.personId === lit} busy={cut.isPending}
                onOpen={() => open(p)} onLight={(on) => setLit(on ? p.personId ?? null : null)}
                onCut={p.personId ? (reason) => cut.mutate({ personId: p.personId!, reason }) : undefined} />
            ))}
          </ol>
          {q.data.alternates.length > 0 && (
            <>
              <h3 className="muted">Next in line</h3>
              <ol className="plain pick-cards alternates" start={q.data.picks.length + 1}>
                {q.data.alternates.map((p, i) => (
                  <PickCard key={`alt:${p.companyId}:${p.personId ?? ''}`} pick={p} rank={q.data.picks.length + i + 1} alternate lit={!!p.personId && p.personId === lit}
                    onOpen={() => open(p)} onLight={(on) => setLit(on ? p.personId ?? null : null)} />
                ))}
              </ol>
            </>
          )}
        </>
      )}
    </section>
  )
}
